import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { analyticsApi, DashboardData } from '../services/api';
import './Dashboard.css';

const COLORS = ['#3498db', '#e74c3c', '#f39c12', '#27ae60', '#9b59b6'];

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsApi.getDashboard(dateRange);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (!data) {
    return <div className="dashboard-error">Error loading dashboard data</div>;
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="date-filters">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
          />
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
          />
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{data.summary.totalCalls}</div>
          <div className="stat-label">Total Calls</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.summary.answeredCalls}</div>
          <div className="stat-label">Answered</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.summary.missedCalls}</div>
          <div className="stat-label">Missed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.summary.answerRate}%</div>
          <div className="stat-label">Answer Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatDuration(data.summary.avgDuration)}</div>
          <div className="stat-label">Avg Duration</div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Calls by Hour</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.charts.callsByHour}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3498db" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Campaign Performance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.charts.callsByCampaign}
                dataKey="total_calls"
                nameKey="campaign_name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                label={({ campaign_name, total_calls }) => `${campaign_name}: ${total_calls}`}
              >
                {data.charts.callsByCampaign.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card full-width">
          <h3>Top Traffic Sources</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.charts.topSources}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="utm_source" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="calls" fill="#27ae60" name="Total Calls" />
              <Bar dataKey="answered" fill="#e74c3c" name="Answered" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;